import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, Package2, Store, Search, Filter, X, ChevronLeft, ChevronRight, ChevronDown, ArrowLeft, CheckCircle2, Truck, MapPin, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { fetchStorefront, submitStoreCheckout, estimateCartTax, estimateCartDeposits, StoreCheckoutResult, StoreCheckoutPayload, StructuredAddress, ShippingMethod, DeliveryAreaInfo, resolveShippingBase, computeShippingSurcharges, describeShippingPrice, rentalRateLabel } from '../../lib/storefront';
import { containerQtyForCheckout, depositPhrase } from '../../lib/deposits';
import { readPortalSession, writePortalSession, subscribePortalSession, fetchPortalOverview, portalPasswordLogin, portalDisplayName, portalLogout, PortalAddress, portalAddressToStructured, savePortalAddress } from '../../lib/portal';
import AddressForm, { emptyAddress, isAddressComplete, addressAreaStatus } from '../../components/store/AddressForm';
import InlineCheckoutPayment from '../../components/store/InlineCheckoutPayment';
import RentalCartNotice from '../../components/store/RentalCartNotice';

interface Business {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  booking_settings?: {
    logo_url?: string;
    [key: string]: any;
  };
}

interface StockItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  quantity: number;
  is_sellable: boolean; // Keep for compatibility but don't filter by it
  track_inventory?: boolean;
  allow_oversell?: boolean;
  tax_exempt?: boolean;
  /** Refundable per-unit container deposit in cents (0 = none). Never taxed. */
  deposit_cents?: number;
  deposit_label?: string | null;
  /** Rented month-to-month instead of sold (rate locks at signup). */
  rental_enabled?: boolean;
  rental_price_cents?: number;
  rental_interval?: 'month' | 'week';
  rental_agreement?: string | null;
  category_id?: string | null;
  category?: {
    id: string;
    name: string;
  } | null;
}

interface CartItem {
  product: StockItem;
  quantity: number;
}

export default function StoreOnline() {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const navigate = useNavigate();
  
  console.log('🏪 StoreOnline component loaded with businessSlug:', businessSlug);
  
  const [business, setBusiness] = useState<Business | null>(null);
  const [products, setProducts] = useState<StockItem[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  
  // Popular products banner state
  const [popularProducts, setPopularProducts] = useState<StockItem[]>([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [isCheckoutView, setIsCheckoutView] = useState(false);
  const [checkoutSummaryOpen, setCheckoutSummaryOpen] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    shippingMethod: '',
    address: '',
    note: ''
  });
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderResult, setOrderResult] = useState<StoreCheckoutResult | null>(null);
  const [storeMeta, setStoreMeta] = useState({
    checkoutMode: 'invoice_on_delivery' as 'invoice_on_delivery' | 'pay_at_checkout',
    onlinePaymentsAvailable: false,
    combinedTaxPercent: 0,
    cardSurchargeEnabled: false,
    cardSurchargePercent: 0,
  });

  // Structured checkout addresses (Shopify-style)
  const [shippingAddress, setShippingAddress] = useState<StructuredAddress>(emptyAddress());
  const [billingSame, setBillingSame] = useState(true);
  const [billingAddress, setBillingAddress] = useState<StructuredAddress>(emptyAddress());
  const [deliveryAreas, setDeliveryAreas] = useState<{ enforced: boolean; prefixes: string[]; areas?: DeliveryAreaInfo[] } | null>(null);
  // Address book (logged-in accounts): pick a saved address or enter a new one.
  const [savedAddresses, setSavedAddresses] = useState<PortalAddress[]>([]);
  // Inline edit of a saved address — persists to the account's address book.
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [editAddr, setEditAddr] = useState<StructuredAddress>(emptyAddress());
  const [savingAddr, setSavingAddr] = useState(false);
  // Containers on hand, split by delivery address when the portal sends it.
  const [containerSites, setContainerSites] = useState<Array<{ stock_item_id: string; quantity: number; customer_address_id?: string | null }>>([]);
  // Wallet credit (dollars) — auto-applied server-side before any card charge.
  const [walletBalance, setWalletBalance] = useState(0);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [saveNewAddress, setSaveNewAddress] = useState(true);
  const [prefilledFromAccount, setPrefilledFromAccount] = useState(false);
  // Logged-in account has B2B terms: checkout invoices on delivery, no payment.
  const [invoiceOnlyAccount, setInvoiceOnlyAccount] = useState(false);
  const [rentalTermsAccepted, setRentalTermsAccepted] = useState(false);
  // Sign-in state surfaced on the checkout page (Shopify-style "Log in" next
  // to Contact). Login from the portal or another tab is picked up via broadcast.
  const [hasPortalSession, setHasPortalSession] = useState(false);
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [checkoutLoginOpen, setCheckoutLoginOpen] = useState(false);
  const [checkoutLogin, setCheckoutLogin] = useState({ email: '', password: '' });
  const [checkoutLoginBusy, setCheckoutLoginBusy] = useState(false);
  const [checkoutLoginError, setCheckoutLoginError] = useState<string | null>(null);
  
  // Store design settings
  const [storeDesign, setStoreDesign] = useState({
    sidebarColor: '#374151', // Default gray-700
    sidebarTextColor: '#ffffff', // Default white
    headerColor: '#ffffff',  // Default white
    backgroundColor: '#f9fafb', // Default gray-50
    accentColor: '#3b82f6',   // Default blue-500
    businessNameColor: '#111827', // Default gray-900
    browseProductsColor: '#6b7280', // Default gray-500
    showBusinessLogo: true,   // Default show logo
    showBusinessName: true,   // Default show name
    showPopularProducts: false, // Default hide popular products
    popularProductIds: [] as string[],
    displayStockLevels: false,   // Default hide stock levels
    hideOutOfStockProducts: false // Default show all products
  });

  // Shipping settings
  const [shippingSettings, setShippingSettings] = useState({
    enableInStorePickup: true,
    pickupAddress: '',
    shippingMethods: [] as ShippingMethod[],
    enableOrderNotes: true
  });

  useEffect(() => {
    if (businessSlug) {
      loadStoreData();
    }
    // Re-run on sign-in/out so the customer's custom/tier prices load.
  }, [businessSlug, hasPortalSession]);

  // Popular products come straight from the loaded catalog
  useEffect(() => {
    if (storeDesign.showPopularProducts && storeDesign.popularProductIds.length > 0) {
      const sorted = (storeDesign.popularProductIds as string[])
        .map(id => products.find(p => p.id === id))
        .filter((p): p is StockItem => !!p);
      setPopularProducts(sorted);
    } else {
      setPopularProducts([]);
    }
  }, [storeDesign.showPopularProducts, storeDesign.popularProductIds, products]);

  // Auto-rotate banner every 5 seconds
  useEffect(() => {
    if (popularProducts.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBannerIndex(prev => (prev + 1) % popularProducts.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [popularProducts.length]);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Everything comes from the public store-catalog function (anon-safe,
      // service-role backed, enforces the store's is_enabled flag). A signed-in
      // portal session personalizes prices (customer custom prices / tier).
      const session = readPortalSession(businessSlug!);
      const data = await fetchStorefront(
        businessSlug!,
        session ? { portalCustomerId: session.customerId, portalToken: session.token } : null
      );

      // Hosted shop turned off (embeds-only store) — same UX as a disabled store.
      if (data.settings.portalShopEnabled === false) {
        setError('This store is not currently accepting online orders');
        return;
      }

      setBusiness({
        id: data.business.id,
        name: data.business.name,
        phone: data.business.phone ?? undefined,
        email: data.business.email ?? undefined,
        address: data.business.address ?? undefined,
        booking_settings: { logo_url: data.business.logoUrl ?? undefined },
      });

      const s = data.settings;
      setStoreDesign({
        sidebarColor: s.sidebarColor,
        sidebarTextColor: s.sidebarTextColor,
        headerColor: s.headerColor,
        backgroundColor: s.backgroundColor,
        accentColor: s.accentColor,
        businessNameColor: s.businessNameColor,
        browseProductsColor: s.browseProductsColor,
        showBusinessLogo: s.showBusinessLogo,
        showBusinessName: s.showBusinessName,
        showPopularProducts: s.showPopularProducts,
        popularProductIds: s.popularProductIds,
        displayStockLevels: s.displayStockLevels,
        hideOutOfStockProducts: s.hideOutOfStockProducts,
      });
      setShippingSettings({
        enableInStorePickup: s.enableInStorePickup,
        pickupAddress: s.pickupAddress,
        shippingMethods: s.shippingMethods,
        enableOrderNotes: s.enableOrderNotes ?? true,
      });
      setStoreMeta({
        checkoutMode: s.checkoutMode,
        onlinePaymentsAvailable: s.onlinePaymentsAvailable,
        combinedTaxPercent: s.combinedTaxPercent,
        cardSurchargeEnabled: s.cardSurchargeEnabled === true,
        cardSurchargePercent: s.cardSurchargePercent || 0,
      });
      setDeliveryAreas(s.deliveryAreas ?? null);

      setProducts(data.products.map(p => ({ ...p, is_sellable: true })));

      const photoUrls: Record<string, string> = {};
      data.products.forEach(p => {
        if (p.photo_url) photoUrls[p.id] = p.photo_url;
      });
      setProductPhotos(photoUrls);

      const categoryMap = new Map();
      data.products.forEach(product => {
        const categoryId = product.category_id || 'uncategorized';
        const categoryName = product.category?.name || 'Uncategorized';
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, { id: categoryId, name: categoryName });
        }
      });
      setCategories(Array.from(categoryMap.values()).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      console.error('🏪 Error loading store data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load store');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product: StockItem) => {
    // Untracked and oversellable items ignore quantity caps entirely.
    const isTracked = product.track_inventory !== false && product.allow_oversell !== true;
    if (isTracked && product.quantity === 0) {
      toast.error('This product is currently out of stock');
      return;
    }

    // Set animation state
    setAddingToCart(product.id);
    
    // Clear animation after 600ms
    setTimeout(() => {
      setAddingToCart(null);
    }, 600);

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      if (existingItem) {
        if (!isTracked || existingItem.quantity < product.quantity) {
          return prevCart.map(item =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        } else {
          toast.error('Not enough stock available');
          return prevCart;
        }
      } else {
        return [...prevCart, { product, quantity: 1 }];
      }
    });
  };

  const updateCartQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity === 0) {
      removeFromCart(productId);
      return;
    }

    const product = products.find(p => p.id === productId);
    if (product && product.track_inventory !== false && product.allow_oversell !== true && newQuantity > product.quantity) {
      toast.error('Not enough stock available');
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.product.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };

  // Rentals price at the recurring rate (first period charged with the order).
  const unitPrice = (p: StockItem) =>
    p.rental_enabled && (p.rental_price_cents ?? 0) > 0 ? (p.rental_price_cents ?? 0) / 100 : p.price;
  const rentalLabel = (p: StockItem) =>
    `$${((p.rental_price_cents ?? 0) / 100).toFixed(2)}/${p.rental_interval === 'week' ? 'wk' : 'mo'}`;
  const isRentalItem = (p: StockItem) => p.rental_enabled === true && (p.rental_price_cents ?? 0) > 0;
  const rentalCartItems = cart.filter(item => isRentalItem(item.product));
  const cartHasRental = rentalCartItems.length > 0;
  const rentalTermsKey = rentalCartItems.map(i => `${i.product.id}:${i.product.rental_agreement || ''}`).join('|');
  useEffect(() => {
    setRentalTermsAccepted(false);
  }, [rentalTermsKey]);
  // Rentals ALWAYS charge a card at checkout. When the rest of the order is
  // invoiced (store mode or invoice-only account), the rental splits into its
  // own card charge — mirrors the server's rental_prepay path.
  const fullCartPaysNow =
    storeMeta.checkoutMode === 'pay_at_checkout' && storeMeta.onlinePaymentsAvailable && !invoiceOnlyAccount;
  const rentalSplit = cartHasRental && !fullCartPaysNow;
  const rentalFirstPeriodTotal = rentalCartItems.reduce(
    (n, i) => n + ((i.product.rental_price_cents ?? 0) / 100) * i.quantity, 0
  );
  // Card charged on this page (Shopify-style inline payment): the whole cart
  // in pay-at-checkout mode, or just the rental first period otherwise.
  const chargeNeeded = fullCartPaysNow || (cartHasRental && storeMeta.onlinePaymentsAvailable);

  const getCartSubtotal = () => {
    return cart.reduce((total, item) => total + (unitPrice(item.product) * item.quantity), 0);
  };

  // Shipping: base price may be flat or area-rated (resolved from the address),
  // plus optional per-product surcharges. Mirrors the server's authoritative math.
  const getSelectedShippingMethod = (): ShippingMethod | null => {
    if (!checkoutForm.shippingMethod.startsWith('shipping-')) return null;
    const idx = parseInt(checkoutForm.shippingMethod.replace('shipping-', ''), 10);
    return shippingSettings.shippingMethods[idx] ?? null;
  };

  const getShippingBase = () => {
    const method = getSelectedShippingMethod();
    return method ? resolveShippingBase(method, shippingAddress, deliveryAreas?.areas) : null;
  };

  const getShippingSurcharges = () => {
    const method = getSelectedShippingMethod();
    if (!method) return [];
    return computeShippingSurcharges(
      method,
      cart.map(item => ({ product: { id: item.product.id, name: item.product.name }, quantity: item.quantity }))
    );
  };

  const getShippingFee = () => {
    const base = getShippingBase();
    return (base?.status === 'priced' ? base.price : 0) + getShippingSurcharges().reduce((n, l) => n + l.amount, 0);
  };

  const getCartTax = () => {
    // Estimate with the business's real tax rates + per-product exemptions.
    // The authoritative amount is recomputed server-side at checkout.
    const itemsTax = estimateCartTax(
      cart.map(item => ({
        product: {
          price: item.product.price,
          tax_exempt: item.product.tax_exempt === true,
          rental_enabled: isRentalItem(item.product),
          rental_price_cents: item.product.rental_price_cents ?? 0,
        },
        quantity: item.quantity,
      })),
      storeMeta.combinedTaxPercent
    );
    const shippingTax = (getShippingFee() * storeMeta.combinedTaxPercent) / 100;
    return itemsTax + (isCheckoutView ? shippingTax : 0);
  };

  // Refundable container deposits (never taxed). Logged-in customers swap
  // empties at delivery, so deposit only applies above their balance.
  const getCartDeposits = () =>
    estimateCartDeposits(
      cart.map(item => ({
        product: {
          id: item.product.id,
          name: item.product.name,
          deposit_cents: item.product.deposit_cents ?? 0,
          deposit_label: item.product.deposit_label,
        },
        quantity: item.quantity,
      })),
      hasPortalSession
        ? containerQtyForCheckout(containerSites, {
            pickup: checkoutForm.shippingMethod === 'pickup',
            addressId: selectedAddressId || null,
          })
        : null
    );

  const getCartTotal = () => {
    return getCartSubtotal() + getCartTax() + getCartDeposits().chargeTotal + (isCheckoutView ? getShippingFee() : 0);
  };

  // Wallet applies at checkout only when the order is charged now (pay-at-
  // checkout, not invoice-only B2B) and the cart has no rental — rentals must
  // capture a card, so those orders pay fully by card (matches the server).
  // Server-side application is authoritative.
  const getWalletCredit = () =>
    hasPortalSession && !invoiceOnlyAccount && !cartHasRental && storeMeta.checkoutMode === 'pay_at_checkout' && storeMeta.onlinePaymentsAvailable
      ? Math.min(walletBalance, getCartTotal())
      : 0;

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const needsAddress = () => checkoutForm.shippingMethod.startsWith('shipping-');

  // Shopify-style order summary shared by the checkout page's sidebar (desktop)
  // and collapsible header (mobile).
  const renderCheckoutSummary = () => (
    <div className="space-y-5">
      <div className="space-y-4">
        {cart.map(item => (
          <div key={item.product.id} className="flex items-center gap-3.5">
            {/* overflow-hidden only on the inner wrapper so the qty badge can overhang */}
            <div className="relative w-16 h-16 flex-shrink-0">
              <div className="w-full h-full bg-white rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
                {productPhotos[item.product.id] ? (
                  <img src={productPhotos[item.product.id]} alt={item.product.name} className="w-full h-full object-cover" />
                ) : (
                  <Package2 className="h-6 w-6 text-gray-300" />
                )}
              </div>
              <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-gray-900 text-white text-[11px] font-bold flex items-center justify-center">
                {item.quantity}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-gray-900 leading-snug">{item.product.name}</p>
              {isRentalItem(item.product) && (
                <p className="text-[11px] text-indigo-700 font-medium">Rental · {rentalLabel(item.product)} until canceled</p>
              )}
            </div>
            <span className="text-[13px] font-medium text-gray-900">${(unitPrice(item.product) * item.quantity).toFixed(2)}</span>
          </div>
        ))}
      </div>
      <div className="space-y-2 pt-5 border-t border-gray-200 text-[13px] text-gray-600">
        <div className="flex justify-between">
          <span>Subtotal · {getCartItemCount()} {getCartItemCount() === 1 ? 'item' : 'items'}</span>
          <span className="font-medium text-gray-900">${getCartSubtotal().toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>Shipping</span>
          <span className="font-medium text-gray-900">
            {(() => {
              if (!checkoutForm.shippingMethod) return '—';
              if (!getSelectedShippingMethod()) return 'FREE';
              const base = getShippingBase();
              if (base?.status === 'needs_address') return 'Enter address';
              if (base?.status === 'unavailable') return 'Unavailable';
              const price = base?.status === 'priced' ? base.price : 0;
              return price > 0 ? `$${price.toFixed(2)}` : 'FREE';
            })()}
          </span>
        </div>
        {getShippingSurcharges().map(l => (
          <div key={l.stockItemId} className="flex justify-between">
            <span>{l.label}</span>
            <span className="font-medium text-gray-900">${l.amount.toFixed(2)}</span>
          </div>
        ))}
        {storeMeta.combinedTaxPercent > 0 && (
          <div className="flex justify-between"><span>Estimated taxes</span><span className="font-medium text-gray-900">${getCartTax().toFixed(2)}</span></div>
        )}
        {getCartDeposits().lines.filter(l => l.charge > 0).map(l => (
          <div key={l.productId} className="flex justify-between">
            <span>{depositPhrase(l.label)} — {l.name}</span>
            <span className="font-medium text-gray-900">${l.charge.toFixed(2)}</span>
          </div>
        ))}
        {hasPortalSession && getCartDeposits().swappedTotal > 0 && (
          <p className="text-[12px] text-emerald-700 leading-snug">
            You have {getCartDeposits().swappedTotal} container{getCartDeposits().swappedTotal === 1 ? '' : 's'} on hand —
            leave your empties out at delivery and no new deposit applies to {getCartDeposits().swappedTotal === 1 ? 'it' : 'them'}.
          </p>
        )}
        <div className="flex justify-between items-baseline pt-3 mt-1.5 border-t border-gray-200">
          <span className="text-base font-bold text-gray-900">Total</span>
          <span className="text-xl font-bold text-gray-900">${getCartTotal().toFixed(2)}</span>
        </div>
        {cartHasRental && (
          <p className="text-[12px] text-indigo-700 leading-snug">
            {rentalSplit ? (
              <>
                Rentals are always paid by card: the first{' '}
                {rentalCartItems.some(i => i.product.rental_interval === 'week') ? 'period' : 'month'} of{' '}
                {rentalCartItems.map(i => `${i.quantity} × ${i.product.name}`).join(', ')} (${rentalFirstPeriodTotal.toFixed(2)}) is
                charged now, then billed automatically each{' '}
                {rentalCartItems.every(i => i.product.rental_interval !== 'week') ? 'month' : 'period'} until canceled.
                The rest of your order is invoiced at delivery as usual.
              </>
            ) : (
              <>
                Includes the first {rentalCartItems.some(i => i.product.rental_interval === 'week') ? 'period' : 'month'} of{' '}
                {rentalCartItems.map(i => `${i.quantity} × ${i.product.name}`).join(', ')} — then{' '}
                ${rentalFirstPeriodTotal.toFixed(2)}
                {rentalCartItems.every(i => i.product.rental_interval !== 'week') ? '/month' : ' per period'} billed
                automatically until canceled. Manage or cancel anytime from your account.
              </>
            )}
          </p>
        )}
        {getWalletCredit() > 0 && (
          <>
            <div className="flex justify-between text-emerald-700">
              <span>Wallet credit</span>
              <span className="font-medium">−${getWalletCredit().toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-sm font-semibold text-gray-900">To pay now</span>
              <span className="text-base font-bold text-gray-900">${Math.max(getCartTotal() - getWalletCredit(), 0).toFixed(2)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Track login/logout from the portal or other tabs.
  useEffect(() => {
    if (!businessSlug) return;
    setHasPortalSession(!!readPortalSession(businessSlug));
    return subscribePortalSession(businessSlug, () => {
      const active = !!readPortalSession(businessSlug);
      setHasPortalSession(active);
      if (active) {
        setPrefilledFromAccount(false); // re-run the account prefill below
      } else {
        setAccountEmail(null);
        setInvoiceOnlyAccount(false);
        setSavedAddresses([]);
        setSelectedAddressId('');
        setContainerSites([]);
        setWalletBalance(0);
      }
    });
  }, [businessSlug]);

  // Prefill contact + saved addresses from the logged-in portal account the
  // first time the checkout view opens.
  useEffect(() => {
    if (!isCheckoutView || prefilledFromAccount || !businessSlug) return;
    const session = readPortalSession(businessSlug);
    if (!session) return;
    setHasPortalSession(true);
    setPrefilledFromAccount(true);
    fetchPortalOverview(businessSlug)
      .then(overview => {
        const p = overview.profile;
        if (!p) return;
        setAccountEmail(p.email || null);
        setInvoiceOnlyAccount(p.invoice_only === true);
        setWalletBalance(Number(p.wallet || 0));
        setContainerSites(overview.containers || []);
        setCheckoutForm(prev => ({
          ...prev,
          firstName: prev.firstName || p.first_name || '',
          lastName: prev.lastName || p.last_name || '',
          email: prev.email || p.email || '',
          phone: prev.phone || p.phone || '',
        }));
        const addresses = overview.addresses || [];
        setSavedAddresses(addresses);
        if (addresses.length > 0) {
          const preferred = addresses.find(a => a.is_default_shipping) || addresses[0];
          setSelectedAddressId(prev => prev || preferred.id);
          setShippingAddress(prev => (prev.line1 ? prev : portalAddressToStructured(preferred)));
        } else if (p.default_shipping_address?.line1) {
          setShippingAddress(prev => (prev.line1 ? prev : { ...emptyAddress(), ...p.default_shipping_address }));
        }
        if (p.default_billing_address?.line1) {
          setBillingAddress(prev => (prev.line1 ? prev : { ...emptyAddress(), ...p.default_billing_address }));
          setBillingSame(false);
        }
      })
      .catch(() => {
        // Stale session — checkout still works as guest.
      });
  }, [isCheckoutView, prefilledFromAccount, businessSlug]);

  const handleCheckoutLogin = async () => {
    if (checkoutLoginBusy || !businessSlug) return;
    setCheckoutLoginBusy(true);
    setCheckoutLoginError(null);
    try {
      const res = await portalPasswordLogin(businessSlug, checkoutLogin.email.trim(), checkoutLogin.password);
      writePortalSession(businessSlug, { token: res.token, customerId: res.customer.id, displayName: portalDisplayName(res.customer) });
      setHasPortalSession(true);
      setCheckoutLoginOpen(false);
      setCheckoutLogin({ email: '', password: '' });
      setPrefilledFromAccount(false); // pull name/email/addresses from the account
    } catch (err) {
      setCheckoutLoginError(err instanceof Error ? err.message : 'Sign in failed — please try again');
    } finally {
      setCheckoutLoginBusy(false);
    }
  };

  const handleCheckoutLogout = () => {
    if (!businessSlug) return;
    void portalLogout(businessSlug);
    setHasPortalSession(false);
    setAccountEmail(null);
    setInvoiceOnlyAccount(false);
  };

  const isCheckoutFormValid = () => {
    const { firstName, lastName, email, phone, shippingMethod } = checkoutForm;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim() || !shippingMethod.trim()) return false;
    if (needsAddress()) {
      if (!isAddressComplete(shippingAddress)) return false;
      if (deliveryAreas?.enforced && addressAreaStatus(shippingAddress, deliveryAreas.prefixes) !== true) return false;
    }
    // Area-priced methods must resolve to a price before the order can be placed.
    const base = getShippingBase();
    if (getSelectedShippingMethod() && base?.status !== 'priced') return false;
    if (!billingSame && !isAddressComplete(billingAddress)) return false;
    // Rentals bill month-to-month against the account — sign-in required and
    // a card must be chargeable (rentals always ride on a card). The agreement
    // is accepted with the checkbox in the rental box above the card.
    if (cartHasRental && (!hasPortalSession || !storeMeta.onlinePaymentsAvailable)) return false;
    return true;
  };

  const handleCheckoutFormChange = (field: string, value: string) => {
    setCheckoutForm(prev => ({ ...prev, [field]: value }));
  };

  const handleProceedToCheckout = () => {
    // Default to the first ship rate (Shopify-style) so the Ship/Pickup toggle
    // has an active side when the checkout page opens.
    setCheckoutForm(f =>
      f.shippingMethod
        ? f
        : { ...f, shippingMethod: shippingSettings.shippingMethods.length ? 'shipping-0' : shippingSettings.enableInStorePickup ? 'pickup' : '' }
    );
    setIsCheckoutView(true);
  };

  const handleBackToCart = () => {
    setIsCheckoutView(false);
  };

  const buildCheckoutPayload = (): StoreCheckoutPayload => {
    const portalSession = readPortalSession(businessSlug!);
    return {
      slug: businessSlug!,
      items: cart.map(item => ({ stock_item_id: item.product.id, quantity: item.quantity })),
      contact: {
        firstName: checkoutForm.firstName.trim(),
        lastName: checkoutForm.lastName.trim(),
        email: checkoutForm.email.trim(),
        phone: checkoutForm.phone.trim(),
      },
      fulfillment: {
        method: checkoutForm.shippingMethod,
        address: needsAddress() ? shippingAddress.formatted || undefined : undefined,
      },
      shippingAddress: needsAddress() ? shippingAddress : null,
      billingAddress: billingSame ? null : billingAddress,
      billingSameAsShipping: billingSame,
      saveShippingAddress: needsAddress() && !!portalSession && selectedAddressId === 'new' && saveNewAddress,
      shippingAddressId: needsAddress() && selectedAddressId && selectedAddressId !== 'new' ? selectedAddressId : null,
      note: checkoutForm.note.trim() || undefined,
      // Logged-in portal customers get the order attached to their account
      // (validated server-side; harmless to omit for guests).
      ...(portalSession ? { portalCustomerId: portalSession.customerId, portalToken: portalSession.token } : {}),
    };
  };

  const handleCheckoutComplete = (result: StoreCheckoutResult) => {
    setOrderResult(result);
    setCart([]);
    setIsCheckoutView(false);
  };

  // No-charge orders only (invoice on delivery). Charged carts go through the
  // inline payment component: pay first, order created after.
  const handlePlaceOrder = async () => {
    if (!businessSlug || placingOrder) return;
    setPlacingOrder(true);
    try {
      const result = await submitStoreCheckout(buildCheckoutPayload());
      handleCheckoutComplete(result);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Checkout failed — please try again');
    } finally {
      setPlacingOrder(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = searchQuery === '' || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const productCategoryId = product.category_id || 'uncategorized';
    const matchesCategory = selectedCategory === 'all' || 
      productCategoryId === selectedCategory;
    
    // Filter out out-of-stock products if the setting is enabled
    // (untracked / made-to-order and oversellable items are always available)
    const hasStock = !storeDesign.hideOutOfStockProducts || product.track_inventory === false || product.allow_oversell === true || product.quantity > 0;
    
    return matchesSearch && matchesCategory && hasStock;
  });

  // Untracked (made-to-order) and oversellable items are always purchasable
  const isOutOfStock = (product: StockItem) =>
    product.track_inventory !== false && product.allow_oversell !== true && product.quantity === 0;

  console.log('🏪 Total products:', products.length);
  console.log('🏪 Filtered products:', filteredProducts.length);
  console.log('🏪 Search query:', searchQuery);
  console.log('🏪 Selected category:', selectedCategory);

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: storeDesign.backgroundColor }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading store...</p>
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: storeDesign.backgroundColor }}
      >
        <div className="text-center max-w-md mx-auto p-6">
          <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Store Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The store you\'re looking for doesn\'t exist.'}</p>
          {businessSlug && (
            <div className="bg-gray-100 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-600">
                <strong>Business Slug:</strong> {businessSlug}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Check the browser console for more details
              </p>
            </div>
          )}
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="h-screen flex flex-col"
      style={{ backgroundColor: storeDesign.backgroundColor }}
    >
      {/* Full Width Header */}
      <div 
        className="relative overflow-hidden flex-shrink-0"
        style={{ 
          background: `linear-gradient(135deg, ${storeDesign.headerColor} 0%, ${storeDesign.headerColor}f0 100%)`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        
        <div className="relative px-6 py-6">
          <div className="flex items-center justify-between">
            {/* Business Info */}
            <div className="flex items-center space-x-4">
              {storeDesign.showBusinessLogo && business?.booking_settings?.logo_url && (
                <div className="h-10 w-10 rounded-xl overflow-hidden flex-shrink-0 shadow-lg">
                  <img 
                    src={business.booking_settings.logo_url} 
                    alt="Business Logo" 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {storeDesign.showBusinessName && business?.name && (
                <div>
                  <h1 
                    className="text-2xl font-bold"
                    style={{ color: storeDesign.businessNameColor }}
                  >
                    {business.name}
                  </h1>
                  <p 
                    className="text-sm"
                    style={{ color: storeDesign.browseProductsColor }}
                  >
                    Browse Products
                  </p>
                </div>
              )}
            </div>

            {/* Enhanced Search Bar */}
            <div className="flex-1 max-w-2xl mx-8 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-6 py-3 bg-white/90 backdrop-blur-sm border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300 shadow-lg placeholder-gray-500"
              />
            </div>
            
            {/* Customer portal (account) link */}
            <button
              onClick={() => navigate(`/${businessSlug}/portal`)}
              className="mr-3 px-4 py-3 rounded-xl bg-white/90 backdrop-blur-sm border border-white/20 text-gray-700 hover:bg-white transition-all duration-300 shadow-lg font-semibold text-sm"
            >
              Account
            </button>

            {/* Enhanced Cart Button */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative text-white px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg backdrop-blur-sm"
              style={{ 
                backgroundColor: storeDesign.accentColor,
                boxShadow: `0 8px 25px ${storeDesign.accentColor}40`
              }}
            >
              <ShoppingCart className="h-5 w-5" />
              <span className="font-semibold">Cart</span>
              {getCartItemCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold shadow-lg animate-pulse">
                  {getCartItemCount()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content Area with Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Enhanced Left Sidebar - Categories */}
      <div 
        className="w-64 flex-shrink-0 relative overflow-hidden"
        style={{ 
          background: `linear-gradient(180deg, ${storeDesign.sidebarColor} 0%, ${storeDesign.sidebarColor}f0 100%)`,
          color: storeDesign.sidebarTextColor,
          boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Subtle texture overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        {/* Categories Navigation */}
        <nav className="relative z-10 p-6 h-full">
          <h3 
            className="text-xs font-semibold uppercase tracking-wider mb-4 opacity-70"
            style={{ color: storeDesign.sidebarTextColor }}
          >
            Categories
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 text-sm font-semibold transform hover:scale-105 ${
                selectedCategory === 'all' 
                  ? 'text-white shadow-lg' 
                  : 'hover:bg-white hover:bg-opacity-10 hover:shadow-md'
              }`}
              style={selectedCategory === 'all' ? {
                backgroundColor: storeDesign.accentColor,
                color: '#ffffff',
                boxShadow: `0 4px 15px ${storeDesign.accentColor}40`
              } : {
                color: storeDesign.sidebarTextColor
              }}
            >
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-current opacity-60"></span>
                <span>All Products</span>
              </div>
            </button>
            {categories.length === 0 ? (
              <div 
                className="text-xs px-3 py-2 opacity-70"
                style={{ color: storeDesign.sidebarTextColor }}
              >
                No categories found
              </div>
            ) : (
              categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 text-sm font-semibold transform hover:scale-105 ${
                    selectedCategory === category.id 
                      ? 'text-white shadow-lg' 
                      : 'hover:bg-white hover:bg-opacity-10 hover:shadow-md'
                  }`}
                  style={selectedCategory === category.id ? {
                    backgroundColor: storeDesign.accentColor,
                    color: '#ffffff',
                    boxShadow: `0 4px 15px ${storeDesign.accentColor}40`
                  } : {
                    color: storeDesign.sidebarTextColor
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 rounded-full bg-current opacity-60"></span>
                    <span>{category.name}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </nav>
      </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">

        {/* Products Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Popular Products Banner - Only show when All Products is selected */}
          {storeDesign.showPopularProducts && selectedCategory === 'all' && popularProducts.length > 0 && (
            <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {/* Light Header */}
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-gray-900">Featured Products</h2>
              </div>

              {/* Light Banner Content */}
              <div className="relative">
                <div className="relative h-52 overflow-hidden">
                  {popularProducts.map((product, index) => (
                    <div
                      key={product.id}
                      className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
                        index === currentBannerIndex ? 'translate-x-0' : 
                        index < currentBannerIndex ? '-translate-x-full' : 'translate-x-full'
                      }`}
                    >
                      <div className="h-full flex items-center p-6 gap-6">
                        {/* Product Image */}
                        <div className="w-36 h-36 rounded-2xl overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0">
                          {productPhotos[product.id] ? (
                            <img
                              src={productPhotos[product.id]}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Package2 className="h-12 w-12 text-gray-400" />
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            {product.name}
                          </h3>
                          {product.description && (
                            <p className="text-gray-600 mb-4 line-clamp-2">
                              {product.description}
                            </p>
                          )}
                          <div className="flex items-center gap-8">
                            <div className="flex items-baseline gap-3">
                              <span 
                                className="text-3xl font-bold"
                                style={{ color: storeDesign.accentColor }}
                              >
                                {isRentalItem(product) ? rentalLabel(product) : `$${product.price.toFixed(2)}`}
                              </span>
                              {isRentalItem(product) && (
                                <span className="text-xs font-bold uppercase tracking-wide text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-1">
                                  Rental
                                </span>
                              )}
                              {!isOutOfStock(product) && (
                                <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded-md font-medium">
                                  In Stock
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => addToCart(product)}
                              disabled={isOutOfStock(product)}
                              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform ${
                                isOutOfStock(product)
                                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                  : addingToCart === product.id
                                  ? 'text-white scale-95 shadow-lg animate-pulse'
                                  : 'text-white hover:scale-105 shadow-lg'
                              }`}
                              style={!isOutOfStock(product) ? { 
                                backgroundColor: addingToCart === product.id ? '#10b981' : storeDesign.accentColor,
                                boxShadow: `0 4px 15px ${addingToCart === product.id ? '#10b981' : storeDesign.accentColor}30`
                              } : {}}
                            >
                              {isOutOfStock(product) 
                                ? 'Out of Stock' 
                                : addingToCart === product.id 
                                ? '✓ Added!' 
                                : 'Add to Cart'
                              }
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Light Navigation Dots */}
                {popularProducts.length > 1 && (
                  <div className="flex justify-center space-x-2 py-4">
                    {popularProducts.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentBannerIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentBannerIndex 
                            ? 'opacity-100' 
                            : 'bg-gray-300 opacity-50 hover:opacity-75'
                        }`}
                        style={index === currentBannerIndex ? { 
                          backgroundColor: storeDesign.accentColor 
                        } : {}}
                      />
                    ))}
                  </div>
                )}

                {/* Light Navigation Arrows */}
                {popularProducts.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentBannerIndex(prev => 
                        prev === 0 ? popularProducts.length - 1 : prev - 1
                      )}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all flex items-center justify-center"
                    >
                      <ChevronLeft className="h-5 w-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => setCurrentBannerIndex(prev => 
                        (prev + 1) % popularProducts.length
                      )}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all flex items-center justify-center"
                    >
                      <ChevronRight className="h-5 w-5 text-gray-600" />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Products Grid */}
          {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h3>
            <p className="text-gray-600">
              {searchQuery || selectedCategory !== 'all' 
                ? 'Try adjusting your search or category filter.'
                : 'No products are currently available.'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredProducts.map(product => (
              <div key={product.id} className="group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 transform">
                {/* Square full-bleed media (Shopify-style) */}
                <div className="relative aspect-square w-full bg-gray-50 overflow-hidden">
                  {productPhotos[product.id] ? (
                    <img
                      src={productPhotos[product.id]}
                      alt={product.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package2 className="h-10 w-10 text-gray-300 group-hover:text-gray-400 transition-colors duration-300" />
                    </div>
                  )}
                  {/* Subtle overlay on hover */}
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                </div>

                {/* Product Info */}
                <div className="p-3">
                  <h3 className="font-medium text-gray-900 mb-1 text-sm line-clamp-1">{product.name}</h3>
                  {product.description && (
                    <p className="text-xs text-gray-600 mb-2 line-clamp-1">{product.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      {isRentalItem(product) ? rentalLabel(product) : `$${product.price.toFixed(2)}`}
                      {isRentalItem(product) && (
                        <span className="ml-1.5 align-middle text-[10px] font-bold uppercase tracking-wide text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-1.5 py-0.5">
                          Rental
                        </span>
                      )}
                    </span>
                    {storeDesign.displayStockLevels && product.track_inventory !== false &&
                      !(product.allow_oversell === true && product.quantity <= 0) && (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        product.quantity === 0 
                          ? 'text-red-600 bg-red-50' 
                          : 'text-green-600 bg-green-50'
                      }`}>
                        {product.quantity === 0 ? 'Out of Stock' : `${product.quantity} left`}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={() => addToCart(product)}
                    disabled={isOutOfStock(product)}
                    className={`w-full py-2.5 px-3 rounded-lg text-sm font-semibold transition-all duration-300 transform ${
                      isOutOfStock(product)
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : addingToCart === product.id
                        ? 'text-white scale-95 shadow-lg animate-pulse'
                        : 'text-white hover:scale-105 shadow-lg'
                    }`}
                    style={!isOutOfStock(product) ? { 
                      backgroundColor: addingToCart === product.id ? '#10b981' : storeDesign.accentColor,
                      boxShadow: `0 4px 15px ${addingToCart === product.id ? '#10b981' : storeDesign.accentColor}30`
                    } : {}}
                  >
                    {isOutOfStock(product) 
                      ? 'Out of Stock' 
                      : addingToCart === product.id 
                      ? '✓ Added!' 
                      : 'Add to Cart'
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Right Side Cart Panel – slides in from right, above all navigation */}
      {isCartOpen && !isCheckoutView && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[99998] transition-opacity"
            onClick={() => setIsCartOpen(false)}
          />
          {/* Panel — h-[100dvh] tracks the visible viewport so the footer stays
              above the mobile browser's bottom toolbar (inset-y-0 alone anchors
              to the layout viewport, which extends behind the toolbar). Older
              browsers without dvh fall back to inset-y-0. */}
          <div className="fixed inset-y-0 right-0 h-[100dvh] z-[99999] w-full sm:w-[420px] max-w-full bg-white shadow-2xl flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white sticky top-0 z-10" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
              <div className="flex items-center gap-3">
                <div 
                  className="p-2 rounded-xl"
                  style={{ backgroundColor: storeDesign.accentColor + '15' }}
                >
                  <ShoppingCart 
                    className="h-5 w-5" 
                    style={{ color: storeDesign.accentColor }}
                  />
                </div>
                <div>
                  <h2 className="text-[17px] font-semibold text-gray-900 leading-tight">Shopping Cart</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{getCartItemCount()} {getCartItemCount() === 1 ? 'item' : 'items'}</p>
                </div>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-5">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                    <ShoppingCart className="h-8 w-8 text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">Your cart is empty</p>
                  <p className="text-xs text-gray-400 mt-1">Browse products to add items</p>
                </div>
              ) : (
                <div className="p-5 space-y-3">
                  {cart.map(item => (
                    <div key={item.product.id} className="flex items-center gap-3 bg-gray-50/80 rounded-xl p-3 border border-gray-100">
                      <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center flex-shrink-0 border border-gray-100 overflow-hidden">
                        {productPhotos[item.product.id] ? (
                          <img
                            src={productPhotos[item.product.id]}
                            alt={item.product.name}
                            className="w-full h-full object-cover rounded-xl"
                          />
                        ) : (
                          <Package2 className="h-6 w-6 text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{item.product.name}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {isRentalItem(item.product) ? `${rentalLabel(item.product)} rental` : `$${item.product.price.toFixed(2)} each`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 bg-white rounded-lg border border-gray-200 px-1 py-0.5">
                        <button
                          onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                          className="p-1 rounded-md hover:bg-gray-100 active:bg-gray-200 transition-colors"
                        >
                          <Minus className="h-3.5 w-3.5 text-gray-600" />
                        </button>
                        <span className="w-6 text-center text-sm font-medium text-gray-900">{item.quantity}</span>
                        <button
                          onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                          className="p-1 rounded-md hover:bg-gray-100 active:bg-gray-200 transition-colors"
                        >
                          <Plus className="h-3.5 w-3.5 text-gray-600" />
                        </button>
                      </div>
                      <div className="text-right flex-shrink-0 min-w-[60px]">
                        <p className="text-sm font-semibold text-gray-900">
                          ${(unitPrice(item.product) * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer with totals and checkout button */}
            {cart.length > 0 && (
              <div className="border-t border-gray-100 bg-white px-5 py-4 space-y-3" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Subtotal</span>
                    <span>${getCartSubtotal().toFixed(2)}</span>
                  </div>
                  {storeMeta.combinedTaxPercent > 0 && (
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>Tax ({storeMeta.combinedTaxPercent}%)</span>
                      <span>${getCartTax().toFixed(2)}</span>
                    </div>
                  )}
                  {getCartDeposits().lines.filter(l => l.charge > 0).map(l => (
                    <div key={l.productId} className="flex items-center justify-between text-sm text-gray-500">
                      <span>{depositPhrase(l.label)} — {l.name}</span>
                      <span>${l.charge.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-gray-100 pt-2 mt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-base font-semibold text-gray-900">Total</span>
                      <span className="text-lg font-bold" style={{ color: storeDesign.accentColor }}>
                        ${getCartTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={handleProceedToCheckout}
                  className="w-full text-white py-3.5 px-6 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-[0.98]"
                  style={{ 
                    backgroundColor: storeDesign.accentColor,
                    boxShadow: `0 2px 12px ${storeDesign.accentColor}30`
                  }}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Proceed to Checkout</span>
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Full-page checkout (Shopify-style) — h-[100dvh] keeps the scrollable
          area (and the pay button at its end) above the mobile browser toolbar. */}
      {isCartOpen && isCheckoutView && (
        <div className="fixed inset-0 h-[100dvh] z-[99999] bg-white overflow-y-auto">
          {/* Top bar */}
          <div className="sticky top-0 z-20 bg-white border-b border-gray-200" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
              <span className="text-base font-bold text-gray-900">{business?.name || 'Checkout'}</span>
              <button
                onClick={() => { setIsCheckoutView(false); setIsCartOpen(false); }}
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                aria-label="Close checkout"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div>
            {/* Mobile order summary accordion */}
            <div className="lg:hidden border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => setCheckoutSummaryOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 sm:px-6 py-3.5"
              >
                <span className="flex items-center gap-1.5 text-[13px] font-medium" style={{ color: storeDesign.accentColor }}>
                  <ShoppingCart className="h-4 w-4" />
                  {checkoutSummaryOpen ? 'Hide' : 'Show'} order summary
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${checkoutSummaryOpen ? 'rotate-180' : ''}`} />
                </span>
                <span className="text-[15px] font-bold text-gray-900">${getCartTotal().toFixed(2)}</span>
              </button>
              {checkoutSummaryOpen && (
                <div className="px-4 sm:px-6 pb-5">
                  {renderCheckoutSummary()}
                </div>
              )}
            </div>

            {/* Shopify-style split: white form pane | full-height gray summary pane */}
            <div className="lg:grid lg:grid-cols-2 lg:min-h-[calc(100vh-3.5rem)]">
              {/* Left: form pane */}
              <div
                className="px-4 sm:px-8 py-7 lg:flex lg:justify-end lg:border-r lg:border-gray-200"
                style={{ paddingBottom: 'calc(1.75rem + env(safe-area-inset-bottom, 0px))' }}
              >
                <div className="w-full lg:max-w-[560px]">
                <button onClick={handleBackToCart} className="flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-800 mb-6">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to cart
                </button>
                <div className="space-y-8">
                  {/* Contact */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-[17px] font-bold text-gray-900">Contact</h3>
                      {!hasPortalSession && (
                        <button
                          onClick={() => setCheckoutLoginOpen(o => !o)}
                          className="text-[13px] font-medium hover:underline"
                          style={{ color: storeDesign.accentColor }}
                        >
                          Log in
                        </button>
                      )}
                    </div>

                    {cartHasRental && !hasPortalSession && (
                      <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 px-3.5 py-2.5 mb-3">
                        <p className="text-[13px] font-semibold text-indigo-900">Sign in to rent equipment</p>
                        <p className="text-[12px] text-indigo-800 mt-0.5">
                          Your cart includes a rental that bills monthly, so it needs to be on your account.
                          Use "Log in" above, or create an account from the store's account menu.
                        </p>
                      </div>
                    )}

                    {hasPortalSession && (
                      <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 mb-3">
                        <span className="flex items-center gap-2 min-w-0 text-[13px] text-gray-700">
                          <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="truncate">Logged in{accountEmail ? ` as ${accountEmail}` : ''}</span>
                        </span>
                        <button onClick={handleCheckoutLogout} className="text-[13px] text-gray-500 hover:text-gray-800 underline flex-shrink-0">
                          Log out
                        </button>
                      </div>
                    )}

                    {!hasPortalSession && checkoutLoginOpen && (
                      <div className="rounded-xl border border-gray-200 p-3.5 mb-3 space-y-2.5">
                        <input
                          type="email"
                          value={checkoutLogin.email}
                          onChange={(e) => setCheckoutLogin(l => ({ ...l, email: e.target.value }))}
                          placeholder="Email"
                          autoComplete="email"
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        />
                        <input
                          type="password"
                          value={checkoutLogin.password}
                          onChange={(e) => setCheckoutLogin(l => ({ ...l, password: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') void handleCheckoutLogin(); }}
                          placeholder="Password"
                          autoComplete="current-password"
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        />
                        {checkoutLoginError && <p className="text-[12px] text-red-600">{checkoutLoginError}</p>}
                        <button
                          onClick={handleCheckoutLogin}
                          disabled={checkoutLoginBusy || !checkoutLogin.email.trim() || !checkoutLogin.password}
                          className="w-full py-2.5 rounded-lg text-white text-[13px] font-semibold disabled:opacity-50"
                          style={{ backgroundColor: storeDesign.accentColor }}
                        >
                          {checkoutLoginBusy ? 'Logging in…' : 'Log in'}
                        </button>
                        <p className="text-[12px] text-gray-500 text-center">
                          Or continue as a guest below — no account needed.
                        </p>
                      </div>
                    )}
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">First Name <span className="text-red-500 font-bold">*</span></label>
                          <input
                            type="text"
                            value={checkoutForm.firstName}
                            onChange={(e) => handleCheckoutFormChange('firstName', e.target.value)}
                            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            placeholder="First name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">Last Name <span className="text-red-500 font-bold">*</span></label>
                          <input
                            type="text"
                            value={checkoutForm.lastName}
                            onChange={(e) => handleCheckoutFormChange('lastName', e.target.value)}
                            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            placeholder="Last name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Email <span className="text-red-500 font-bold">*</span></label>
                        <input
                          type="email"
                          value={checkoutForm.email}
                          onChange={(e) => handleCheckoutFormChange('email', e.target.value)}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                          placeholder="Email address"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Phone <span className="text-red-500 font-bold">*</span></label>
                        <input
                          type="tel"
                          value={checkoutForm.phone}
                          onChange={(e) => handleCheckoutFormChange('phone', e.target.value)}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                          placeholder="Phone number"
                        />
                      </div>
                      {shippingSettings.enableOrderNotes && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">Delivery instructions</label>
                          <textarea
                            value={checkoutForm.note}
                            onChange={(e) => handleCheckoutFormChange('note', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                            placeholder="Gate code, where to leave the order, buzzer number… (optional)"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Delivery */}
                  <div>
                    <h3 className="text-[17px] font-bold text-gray-900 mb-3">Delivery</h3>

                    {/* Ship / Pickup segmented toggle (only when both exist) */}
                    {shippingSettings.enableInStorePickup && shippingSettings.shippingMethods.length > 0 && (
                      <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1 mb-3">
                        <button
                          type="button"
                          onClick={() => handleCheckoutFormChange('shippingMethod', checkoutForm.shippingMethod.startsWith('shipping-') ? checkoutForm.shippingMethod : 'shipping-0')}
                          className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            checkoutForm.shippingMethod !== 'pickup' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Truck className="h-4 w-4" /> Ship
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCheckoutFormChange('shippingMethod', 'pickup')}
                          className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            checkoutForm.shippingMethod === 'pickup' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <MapPin className="h-4 w-4" /> Pickup
                        </button>
                      </div>
                    )}

                    {checkoutForm.shippingMethod === 'pickup' ? (
                      shippingSettings.enableInStorePickup && (
                        <div className="flex items-center justify-between rounded-xl border-2 bg-blue-50/30 px-3.5 py-3" style={{ borderColor: storeDesign.accentColor }}>
                          <div className="flex items-center gap-2.5 text-sm font-medium text-gray-900">
                            <span className="w-[18px] h-[18px] rounded-full bg-white border-[5px] flex-shrink-0" style={{ borderColor: storeDesign.accentColor }} />
                            <span>{shippingSettings.pickupAddress || business?.address || 'Pickup address not set'}</span>
                          </div>
                          <span className="text-[13px] font-semibold text-green-600">FREE</span>
                        </div>
                      )
                    ) : shippingSettings.shippingMethods.length === 1 ? (
                      (() => {
                        const d = describeShippingPrice(shippingSettings.shippingMethods[0], shippingAddress, deliveryAreas?.areas);
                        return (
                          <div className="flex items-center justify-between rounded-xl border-2 bg-blue-50/30 px-3.5 py-3" style={{ borderColor: storeDesign.accentColor }}>
                            <div className="flex items-center gap-2.5 text-sm font-medium text-gray-900">
                              <span className="w-[18px] h-[18px] rounded-full bg-white border-[5px] flex-shrink-0" style={{ borderColor: storeDesign.accentColor }} />
                              <span>{shippingSettings.shippingMethods[0].name}</span>
                            </div>
                            <span className={`text-[13px] font-semibold ${
                              d.state === 'free' ? 'text-green-600' : d.state === 'priced' ? 'text-gray-900' : d.state === 'unavailable' ? 'text-red-500' : 'text-gray-400'
                            }`}>
                              {d.state === 'needs_address' ? 'Enter address below' : d.label}
                            </span>
                          </div>
                        );
                      })()
                    ) : shippingSettings.shippingMethods.length > 1 ? (
                      <div className="space-y-2">
                        {shippingSettings.shippingMethods.map((method, index) => {
                          const d = describeShippingPrice(method, shippingAddress, deliveryAreas?.areas);
                          const unavailable = d.state === 'unavailable';
                          return (
                            <label
                              key={index}
                              className={`flex items-center justify-between rounded-xl border px-3.5 py-3 ${unavailable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${checkoutForm.shippingMethod === `shipping-${index}` ? 'bg-blue-50/40' : 'border-gray-200'}`}
                              style={checkoutForm.shippingMethod === `shipping-${index}` ? { borderColor: storeDesign.accentColor } : {}}
                            >
                              <span className="flex items-center gap-2.5 text-sm text-gray-800">
                                <input
                                  type="radio"
                                  disabled={unavailable}
                                  checked={checkoutForm.shippingMethod === `shipping-${index}`}
                                  onChange={() => handleCheckoutFormChange('shippingMethod', `shipping-${index}`)}
                                  style={{ accentColor: storeDesign.accentColor }}
                                />
                                {method.name}
                              </span>
                              <span className={`text-[13px] font-semibold ${
                                d.state === 'free' ? 'text-green-600' : d.state === 'priced' ? 'text-gray-900' : unavailable ? 'text-red-500' : 'text-gray-400'
                              }`}>
                                {d.state === 'needs_address' ? 'Enter address below' : d.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : !shippingSettings.enableInStorePickup ? (
                      <div className="text-center py-8 text-gray-400">
                        <Package2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No shipping methods available</p>
                        <p className="text-xs mt-1">Please contact the store for assistance</p>
                      </div>
                    ) : null}
                  </div>

                  {/* Delivery Address */}
                  {needsAddress() && (
                    <div>
                      <h3 className="text-[17px] font-bold text-gray-900 mb-3">Delivery address</h3>
                      {hasPortalSession && savedAddresses.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {savedAddresses.map(a => {
                            const active = selectedAddressId === a.id;
                            const summary = [a.line1, a.line2, a.city, [a.province, a.postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                            return (
                              <div key={a.id}>
                                <label
                                  className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-3 cursor-pointer ${active ? 'bg-blue-50/40' : 'border-gray-200'}`}
                                  style={active ? { borderColor: storeDesign.accentColor } : {}}
                                >
                                  <input
                                    type="radio"
                                    checked={active}
                                    onChange={() => {
                                      setSelectedAddressId(a.id);
                                      setShippingAddress(portalAddressToStructured(a));
                                    }}
                                    className="mt-0.5"
                                    style={{ accentColor: storeDesign.accentColor }}
                                  />
                                  <span className="text-sm text-gray-800 flex-1 min-w-0">
                                    <span className="font-medium text-gray-900">
                                      {a.label || a.company || 'Saved address'}
                                      {a.is_default_shipping && <span className="ml-1.5 text-[11px] font-semibold text-gray-400 uppercase">Default</span>}
                                    </span>
                                    <span className="block text-[13px] text-gray-500">{summary}</span>
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (editingAddressId === a.id) { setEditingAddressId(null); return; }
                                      setEditingAddressId(a.id);
                                      setEditAddr(portalAddressToStructured(a));
                                    }}
                                    className="text-[12px] font-semibold flex-shrink-0"
                                    style={{ color: storeDesign.accentColor }}
                                  >
                                    {editingAddressId === a.id ? 'Close' : 'Edit'}
                                  </button>
                                </label>
                                {editingAddressId === a.id && (
                                  <div className="mt-2 ml-1 rounded-xl border border-gray-200 p-3 space-y-3">
                                    <AddressForm
                                      value={editAddr}
                                      onChange={setEditAddr}
                                      inputClass="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                      areaPrefixes={deliveryAreas?.enforced ? deliveryAreas.prefixes : null}
                                      accent={storeDesign.accentColor}
                                      idPrefix={`store-edit-${a.id}`}
                                    />
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        disabled={savingAddr || !isAddressComplete(editAddr)}
                                        onClick={async () => {
                                          if (!businessSlug) return;
                                          setSavingAddr(true);
                                          try {
                                            await savePortalAddress(businessSlug, { id: a.id, address: editAddr });
                                            setSavedAddresses(prev => prev.map(x => x.id === a.id ? {
                                              ...x,
                                              company: editAddr.company || null,
                                              line1: editAddr.line1,
                                              line2: editAddr.line2 || null,
                                              city: editAddr.city,
                                              province: editAddr.province,
                                              postal_code: editAddr.postal_code,
                                              country: editAddr.country,
                                            } : x));
                                            if (selectedAddressId === a.id) setShippingAddress({ ...editAddr });
                                            setEditingAddressId(null);
                                          } catch {
                                            // keep the form open so nothing is lost
                                          } finally {
                                            setSavingAddr(false);
                                          }
                                        }}
                                        className="px-4 py-2 rounded-xl text-white text-[13px] font-semibold disabled:opacity-50"
                                        style={{ background: storeDesign.accentColor }}
                                      >
                                        {savingAddr ? 'Saving…' : 'Save address'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setEditingAddressId(null)}
                                        className="px-3 py-2 rounded-xl text-[13px] font-semibold text-gray-600 hover:bg-gray-100"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <label
                            className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-3 cursor-pointer ${selectedAddressId === 'new' ? 'bg-blue-50/40' : 'border-gray-200'}`}
                            style={selectedAddressId === 'new' ? { borderColor: storeDesign.accentColor } : {}}
                          >
                            <input
                              type="radio"
                              checked={selectedAddressId === 'new'}
                              onChange={() => {
                                setSelectedAddressId('new');
                                setShippingAddress(emptyAddress());
                              }}
                              style={{ accentColor: storeDesign.accentColor }}
                            />
                            <span className="text-sm font-medium text-gray-900">Use a new address</span>
                          </label>
                        </div>
                      )}
                      {(!hasPortalSession || savedAddresses.length === 0 || selectedAddressId === 'new') && (
                        <AddressForm
                          value={shippingAddress}
                          onChange={setShippingAddress}
                          inputClass="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                          areaPrefixes={deliveryAreas?.enforced ? deliveryAreas.prefixes : null}
                          accent={storeDesign.accentColor}
                          idPrefix="store-ship"
                        />
                      )}
                      {hasPortalSession && selectedAddressId === 'new' && (
                        <label className="flex items-center gap-2 pt-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={saveNewAddress}
                            onChange={e => setSaveNewAddress(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300"
                            style={{ accentColor: storeDesign.accentColor }}
                          />
                          <span className="text-sm text-gray-700">Save this address to my account</span>
                        </label>
                      )}
                    </div>
                  )}

                  {/* Billing Address */}
                  {needsAddress() && (
                    <div>
                      <h3 className="text-[17px] font-bold text-gray-900 mb-3">Billing address</h3>
                      <label className="flex items-center gap-2.5 mb-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={billingSame}
                          onChange={(e) => setBillingSame(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300"
                          style={{ accentColor: storeDesign.accentColor }}
                        />
                        <span className="text-sm text-gray-700">Same as delivery address</span>
                      </label>
                      {!billingSame && (
                        <AddressForm
                          value={billingAddress}
                          onChange={setBillingAddress}
                          inputClass="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                          idPrefix="store-bill"
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-8">
                  {cartHasRental && (
                    <RentalCartNotice
                      rentals={rentalCartItems.map(i => ({
                        name: i.product.name,
                        quantity: i.quantity,
                        rateLabel: rentalRateLabel(i.product),
                        interval: i.product.rental_interval === 'week' ? 'week' as const : 'month' as const,
                        agreement: i.product.rental_agreement,
                      }))}
                      businessName={business?.name || 'the store'}
                      firstPeriodLabel={`$${rentalFirstPeriodTotal.toFixed(2)}`}
                      splitCharge={rentalSplit}
                      agreed={rentalTermsAccepted}
                      onAgreedChange={setRentalTermsAccepted}
                      accent={storeDesign.accentColor}
                    />
                  )}
                  {invoiceOnlyAccount && storeMeta.checkoutMode === 'pay_at_checkout' && !cartHasRental && (
                    <p className="text-[13px] text-gray-500 mb-3">
                      Your account is invoiced — you'll receive an invoice after delivery.
                    </p>
                  )}
                  {rentalSplit && storeMeta.onlinePaymentsAvailable && (
                    <p className="text-[13px] text-indigo-700 mb-3">
                      Your rental (${rentalFirstPeriodTotal.toFixed(2)} first period) is paid by card now and billed
                      automatically each period — the rest of your order is invoiced after delivery as usual.
                    </p>
                  )}
                  {cartHasRental && !storeMeta.onlinePaymentsAvailable && (
                    <p className="text-[13px] text-red-600 mb-3">
                      Rentals are paid by card and this store doesn't accept online payments yet — please remove
                      the rental item or contact the store to set up your rental.
                    </p>
                  )}
                  {chargeNeeded && business?.id ? (
                    <InlineCheckoutPayment
                      businessId={business.id}
                      businessName={business?.name || 'the store'}
                      accent={storeDesign.accentColor}
                      disabled={!isCheckoutFormValid()}
                      buildPayload={buildCheckoutPayload}
                      chargeKind={fullCartPaysNow ? 'full' : 'rental_split'}
                      estimatedChargeCents={Math.round(
                        (fullCartPaysNow
                          ? Math.max(getCartTotal() - getWalletCredit(), 0)
                          : rentalFirstPeriodTotal +
                            (rentalCartItems
                              .filter(i => i.product.tax_exempt !== true)
                              .reduce((n, i) => n + ((i.product.rental_price_cents ?? 0) / 100) * i.quantity, 0) *
                              storeMeta.combinedTaxPercent) / 100) * 100
                      )}
                      surcharge={{
                        enabled: storeMeta.cardSurchargeEnabled,
                        percent: storeMeta.cardSurchargePercent,
                      }}
                      rentals={rentalCartItems.map(i => ({
                        name: i.product.name,
                        quantity: i.quantity,
                        rateLabel: rentalRateLabel(i.product),
                        interval: i.product.rental_interval === 'week' ? 'week' as const : 'month' as const,
                        agreement: i.product.rental_agreement,
                      }))}
                      termsAccepted={rentalTermsAccepted}
                      rentalFirstPeriodLabel={`$${rentalFirstPeriodTotal.toFixed(2)}`}
                      onComplete={handleCheckoutComplete}
                    />
                  ) : (
                    <button
                      onClick={handlePlaceOrder}
                      disabled={!isCheckoutFormValid() || placingOrder}
                      className={`w-full py-4 rounded-lg text-base font-semibold transition-all duration-200 ${
                        isCheckoutFormValid() && !placingOrder
                          ? 'text-white active:scale-[0.99]'
                          : 'text-gray-400 cursor-not-allowed bg-gray-200'
                      }`}
                      style={isCheckoutFormValid() && !placingOrder ? {
                        backgroundColor: storeDesign.accentColor,
                        boxShadow: `0 2px 12px ${storeDesign.accentColor}30`
                      } : {}}
                    >
                      {placingOrder ? 'Placing order…' : 'Place order'}
                    </button>
                  )}
                </div>
                </div>
              </div>

              {/* Right: full-height gray order summary pane */}
              <aside className="hidden lg:block bg-gray-50">
                <div className="sticky top-14 px-8 py-7 max-w-[480px]">
                  {renderCheckoutSummary()}
                </div>
              </aside>
            </div>
          </div>
        </div>
      )}

      {/* Order confirmation */}
      {orderResult && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-9 w-9 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Order placed!</h2>
            <p className="text-sm font-semibold mb-3" style={{ color: storeDesign.accentColor }}>
              {orderResult.orderNumber}
            </p>
            <p className="text-sm text-gray-600 mb-1">
              Estimated total: <span className="font-semibold text-gray-900">${(orderResult.totals.totalCents / 100).toFixed(2)}</span>
            </p>
            {(orderResult.walletAppliedCents ?? 0) > 0 && (
              <p className="text-sm text-emerald-700 mb-1">
                ${((orderResult.walletAppliedCents ?? 0) / 100).toFixed(2)} was paid from your wallet balance.
              </p>
            )}
            <p className="text-sm text-gray-500 mb-2">
              {orderResult.mode === 'paid_from_wallet'
                ? 'Your order is fully paid — no payment is due at delivery or pickup.'
                : orderResult.mode === 'pay_at_checkout' && orderResult.paid
                ? 'Payment received — thank you! A confirmation will be sent to your email.'
                : orderResult.mode === 'rental_prepay' && orderResult.paid
                ? `Rental payment received — your card will be billed automatically each period. The rest of your order will be invoiced by ${business?.name} at delivery.`
                : `${business?.name} has received your order and will confirm it shortly.
              You'll settle up when your order is delivered or picked up.`}
            </p>
            {(orderResult.rentals?.length ?? 0) > 0 && (
              <p className="text-sm text-indigo-700 mb-6">
                Your rental{orderResult.rentals!.length === 1 ? '' : 's'} —{' '}
                {orderResult.rentals!.map(r => `${r.quantity} × ${r.product_name}`).join(', ')} — start
                {orderResult.rentals!.length === 1 ? 's' : ''} at delivery and bill
                {orderResult.rentals!.length === 1 ? 's' : ''} automatically each {orderResult.rentals![0].interval}.
                Manage or cancel anytime from your account.
              </p>
            )}
            {(orderResult.rentals?.length ?? 0) === 0 && <span className="block mb-4" />}
            <button
              onClick={() => { setOrderResult(null); setIsCartOpen(false); }}
              className="w-full py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98]"
              style={{ backgroundColor: storeDesign.accentColor }}
            >
              Continue shopping
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
